<div #tableContainer class="custom-table-container">
  <!-- Header Actions -->
  <div class="header-actions">
    <div class="search-box" *ngIf="showSearch">
      <mat-icon>search</mat-icon>
      <input type="text" 
             placeholder="Search..." 
             (input)="onSearch($event)"
             [disabled]="loading">
    </div>
    <div class="action-buttons">
      <!-- Refresh button -->
      <button *ngIf="showRefresh" 
              mat-icon-button 
              class="refresh-button"
              (click)="onRefresh()"
              [disabled]="loading">
        <mat-icon>refresh</mat-icon>
      </button>
      
      <!-- Toggle Column Filters button -->
      <button *ngIf="showColumnFilters" 
              mat-button 
              class="column-menu-button"
              (click)="toggleFilters()"
              [disabled]="loading"
              [matTooltip]="filtersVisible ? 'Hide Column Filters' : 'Show Column Filters'">
        <mat-icon>{{ filtersVisible ? 'filter_alt' : 'filter_alt_off' }}</mat-icon>
        Filters
      </button>
      
      <!-- Column visibility menu -->
      <button *ngIf="showColumnToggle" 
              mat-button 
              [matMenuTriggerFor]="columnMenu" 
              class="column-menu-button"
              [disabled]="loading">
        <mat-icon>view_column</mat-icon>
        Columns
      </button>
      <mat-menu #columnMenu="matMenu" class="column-menu">
        <div class="column-menu-header">
          <mat-icon class="header-icon">view_column</mat-icon>
          <span class="header-title">Column Settings</span>
        </div>
        <div class="column-menu-items">
          <div *ngFor="let col of allColumns" class="column-menu-item" (click)="$event.stopPropagation()">
            <div class="column-menu-item-content">
              <mat-checkbox [(ngModel)]="col.visible" 
                           (change)="updateDisplayedColumns()"
                           [disabled]="col.key === 'checkbox' && selectionMode !== 'none' || loading">
                {{col.title}}
              </mat-checkbox>
              <button *ngIf="col.visible && col.key !== 'checkbox' && col.key !== 'actions'" 
                      mat-icon-button 
                      class="pin-button"
                      [class.pinned]="col.pinned"
                      (click)="toggleColumnPin(col); $event.stopPropagation()"
                      [matTooltip]="col.pinned ? 'Unpin Column' : 'Pin Column'"
                      [disabled]="loading">
                <mat-icon style="font-size: 16px !important; width: 16px !important; height: 16px !important;">{{ col.pinned ? 'lock' : 'lock_open' }}</mat-icon>
              </button>
            </div>
          </div>
        </div>
      </mat-menu>

      <button *ngIf="showExport" 
              class="btn-export" 
              (click)="onExport()"
              [disabled]="loading || data.length === 0">
        <mat-icon>download</mat-icon>
        Export
      </button>
      <ng-content select="[customActions]"></ng-content>
    </div>
  </div>

  <!-- Table -->
  <div class="table-wrapper" [class.has-pinned-columns]="pinnedColumns.length > 0">
    <table [class.filters-visible]="showColumnFilters && filtersVisible">
      <thead>
        <tr cdkDropList cdkDropListOrientation="horizontal" (cdkDropListDropped)="onColumnDrop($event)">
          <th *ngFor="let col of visibleColumns; trackBy: trackByColumnKey" 
              class="column-header column-header-left" 
              [class.pinned-column]="col.pinned"
              [style.width.px]="col.width ?? defaultColumnWidth"
              [style.left.px]="col.pinned ? getPinnedColumnLeft(col) : 'auto'"
              [matTooltip]="col.headerTooltip || (pinnedZoneCount >= 0 && col.key !== 'checkbox' && col.key !== 'actions' ? 'Drag to reorder; drag to left to pin (freeze), drag out to unpin' : '')">
            <div cdkDrag [cdkDragDisabled]="isResizing || loading" class="column-header-content">
              <div class="column-title" cdkDragHandle (click)="onSort(col)">
                <mat-icon *ngIf="col.headerIcon" class="header-icon">{{ col.headerIcon }}</mat-icon>
                <span *ngIf="!col.headerIcon">{{ col.title }}</span>
                <mat-icon *ngIf="showColumnFilters && col.key !== 'checkbox' && col.key !== 'actions' && col.filterable !== false" 
                          class="column-filter-icon"
                          [matTooltip]="filtersVisible ? 'Click to hide column filters' : 'Click to show column filters'"
                          (click)="onColumnFilterIconClick(col.key); $event.stopPropagation()">filter_list</mat-icon>
                <div *ngIf="col.sortable" class="sort-icons">
                  <mat-icon 
                    [fontIcon]="currentSortColumn === col.key && sortDirection === 'asc' ? 'arrow_upward' : 
                              (currentSortColumn === col.key && sortDirection === 'desc' ? 'arrow_downward' : 'unfold_more')"
                    [class.active-sort]="currentSortColumn === col.key && sortDirection !== ''">
                  </mat-icon>
                </div>
              </div>
              <input *ngIf="col.key !== 'checkbox' && col.key !== 'actions' && showColumnFilters && filtersVisible && col.filterable !== false" 
                     type="text" 
                     [id]="'col-filter-' + col.key"
                     class="column-search"
                     (input)="applyColumnFilter($event, col.key)"
                     [disabled]="loading"
                     [placeholder]="'Filter ' + col.title">
            </div>
            <div *cdkDragPreview class="column-drag-preview">
              {{ col.title }}
            </div>
            <div *ngIf="showColumnResize" class="resize-handle" 
                 (mousedown)="onResizeStart($event, col); $event.stopPropagation()"></div>
          </th>
        </tr>
      </thead>
      <tbody>
        <tr *ngIf="loading">
          <td [attr.colspan]="visibleColumns.length" class="loading-row">
            <div class="loading-spinner">
              <mat-icon class="spinner">autorenew</mat-icon>
              Loading data...
            </div>
          </td>
        </tr>
        
        <tr *ngFor="let row of paginatedData; let i = index" 
            (click)="onRowClick(row)"
            [class.clickable-row]="rowClickable && !loading"
            [class.selected-row]="isRowSelected(row)"
            [ngClass]="rowClass ? rowClass(row) : ''">
          <td *ngFor="let col of visibleColumns; trackBy: trackByColumnKey" 
              [class.pinned-column]="col.pinned"
              [style.width.px]="col.width ?? defaultColumnWidth" 
              [style.left.px]="col.pinned ? getPinnedColumnLeft(col) : 'auto'"
              [ngClass]="{
                'action-cell': col.key === 'actions',
                'text-left': col.align === 'left',
                'text-center': col.align === 'center',
                'text-right': col.align === 'right'
              }">
            <ng-container [ngSwitch]="col.key">
              <ng-container *ngSwitchCase="'checkbox'">
                <input type="checkbox" 
                       [checked]="isRowSelected(row)"
                       (change)="onRowSelect(row, $event)"
                       (click)="$event.stopPropagation()"
                       [disabled]="loading">
              </ng-container>
              <ng-container *ngSwitchCase="'actions'">
                <ng-container *ngIf="col.cellTemplate; else actionsButtons">
                  <ng-container *ngTemplateOutlet="col.cellTemplate; context: {$implicit: row, col: col, index: i, rowIndex: (currentPage - 1) * itemsPerPage + i}"></ng-container>
                </ng-container>
                <ng-template #actionsButtons>
                  <div class="row-action-buttons">
                    <ng-container *ngTemplateOutlet="actionsTemplate || defaultActionsTemplate; context: {$implicit: row}"></ng-container>
                  </div>
                </ng-template>
              </ng-container>
              <ng-container *ngSwitchDefault>
                <ng-container *ngIf="col.cellTemplate; else defaultCell">
                  <ng-container *ngTemplateOutlet="col.cellTemplate; context: {$implicit: row, col: col, index: i, rowIndex: (currentPage - 1) * itemsPerPage + i}"></ng-container>
                </ng-container>
                <ng-template #defaultCell>
                  <span *ngIf="col.clickable && col.onClick" 
                        class="clickable-cell" 
                        (click)="col.onClick(row); $event.stopPropagation()">
                    {{ col.format ? col.format(getRowValue(row, col.key), row) : getRowValue(row, col.key) }}
                  </span>
                  <span *ngIf="!col.clickable || !col.onClick">
                    {{ col.format ? col.format(getRowValue(row, col.key), row) : getRowValue(row, col.key) }}
                  </span>
                </ng-template>
              </ng-container>
            </ng-container>
          </td>
        </tr>
        
      </tbody>
    </table>
    <div *ngIf="!loading && paginatedData.length === 0"
         class="table-empty-overlay"
         role="status"
         aria-live="polite">
      {{ noDataMessage }}
    </div>
  </div>

  <!-- Pagination -->
  <div class="pagination" *ngIf="showPagination && !loading">
    <div class="items-per-page">
      Items per page:
      <select [(ngModel)]="itemsPerPage" (change)="onItemsPerPageChange()">
        <option *ngFor="let option of pageSizeOptions" [value]="option">{{option}}</option>
      </select>
    </div>
    <div class="page-info">
      <ng-container *ngIf="hasActiveFilters; else defaultPageInfo">
        {{ filteredItemCount }} / {{ totalRecordCount }}
        ({{ pageRangeStart }} - {{ pageRangeEnd }} of {{ filteredItemCount }})
      </ng-container>
      <ng-template #defaultPageInfo>
        {{ pageRangeStart }} - {{ pageRangeEnd }} of {{ totalRecordCount }}
      </ng-template>
    </div>
    <div class="page-navigation">
      <button (click)="previousPage()" [disabled]="!hasPreviousPage">&lt;</button>
      <button (click)="nextPage()" [disabled]="!hasNextPage">&gt;</button>
    </div>
  </div>

  <ng-template #defaultActionsTemplate let-row>
    <button *ngIf="showDefaultActions" 
            mat-icon-button 
            (click)="onEditRow(row); $event.stopPropagation()" 
            class="btn-edit"
            [disabled]="loading">
      <mat-icon>edit</mat-icon>
    </button>
    <button *ngIf="showDefaultActions" 
            mat-icon-button 
            (click)="onDeleteRow(row); $event.stopPropagation()" 
            class="btn-delete"
            [disabled]="loading">
      <mat-icon>delete</mat-icon>
    </button>
  </ng-template>
</div>
